import type { Dicom } from '@/models/dicom';

/**
 * Resolve a multi-frame DICOM's native frame rate (FPS), or `0` if it can't be
 * determined yet.
 *
 * Order of preference:
 * 1. `framesPerSecond` — backend-computed native rate.
 * 2. `frameTime` — derive from ms-per-frame.
 * 3. `numberOfFrames / videoDurationSec` — when the backend sends no frame
 *    timing, extract it on the frontend from the decoded video (every frame is
 *    encoded across the clip's duration). This matches how Cornerstone's
 *    VideoViewport computes its own frame rate.
 *
 * Returns `0` when no source is available (e.g. the video hasn't loaded yet),
 * so callers can hide/disable the FPS UI instead of showing a misleading value.
 */
export function getNativeFps(
  dicom: Pick<Dicom, 'framesPerSecond' | 'frameTime' | 'numberOfFrames'>,
  videoDurationSec?: number
): number {
  if (dicom.framesPerSecond > 0) return dicom.framesPerSecond;
  if (dicom.frameTime > 0) return 1000 / dicom.frameTime;
  if (
    videoDurationSec !== undefined &&
    videoDurationSec > 0 &&
    dicom.numberOfFrames > 1
  ) {
    return dicom.numberOfFrames / videoDurationSec;
  }
  return 0;
}
